// Columna izquierda del explorer (`.col-filters` del mock): Búsqueda · Tienda · Días de mora ·
// Canal último contacto · Monto vencido · Extra, con el contador de filtros puestos en el encabezado.
import { useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { FiltersColumn } from '@/shared/patterns/FiltersColumn';
import { useDebounce } from '@/shared/hooks/useDebounce';
import { cn } from '@/lib/cn';
import { CONTACT_CHANNEL_FILTER_OPTIONS } from '../format';
import { activeFilterCount } from '../logic';
import { AGENDA_STORE_OPTIONS } from '../fixtures';
import {
    emptyAgendaFilters,
    type AgendaFilters,
    type AgendaFlag,
    type ArrearsBucket,
} from '../types';

const LABEL = 'mb-[5px] text-[9.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const GROUP = 'mt-2.5';
const FIELD =
    'h-[26px] w-full rounded border border-border bg-card px-2 text-[11.5px] text-foreground outline-none focus-visible:border-ring';

/** Los tramos del combo «Días de mora», con el rótulo del mock. */
const ARREARS_OPTIONS: readonly { value: ArrearsBucket; label: string }[] = [
    { value: '1-7', label: '1–7 días' },
    { value: '8-30', label: '8–30 días' },
    { value: '31-60', label: '31–60 días' },
    { value: '61-90', label: '61–90 días' },
    { value: '90+', label: '90+ días' },
];

/** Los tres interruptores del grupo «Extra». */
const FLAG_OPTIONS: readonly { value: AgendaFlag; label: string; danger?: boolean }[] = [
    { value: 'with_promise', label: 'Con compromiso activo' },
    { value: 'broken_promise', label: 'Compromiso incumplido' },
    { value: 'locked_device', label: 'Dispositivo bloqueado', danger: true },
];

/** Un campo en PESOS que sube a centavos; vacío es `null` («sin tope»), no cero. */
function MoneyInput({
    value,
    placeholder,
    ariaLabel,
    onChange,
}: {
    value: number | null;
    placeholder: string;
    ariaLabel: string;
    onChange: (cents: number | null) => void;
}) {
    return (
        <input
            type="number"
            min={0}
            inputMode="numeric"
            aria-label={ariaLabel}
            placeholder={placeholder}
            value={value === null ? '' : String(value / 100)}
            onChange={(event) => {
                const raw = event.target.value.trim();
                onChange(raw === '' ? null : Math.round(Number(raw) * 100));
            }}
            className={cn(FIELD, 'tabular-nums')}
        />
    );
}

export interface AgendaFiltersPanelProps {
    filters: AgendaFilters;
    onChange: (next: AgendaFilters) => void;
    collapsed: boolean;
    onToggleCollapsed: () => void;
}

export function AgendaFiltersPanel({
    filters,
    onChange,
    collapsed,
    onToggleCollapsed,
}: AgendaFiltersPanelProps) {
    const [searchText, setSearchText] = useState(filters.search);
    const debouncedSearch = useDebounce(searchText, 250);
    const emittedSearch = useRef(filters.search);

    // Sube la búsqueda ya reposada; el guard evita re-emitir cuando cambia otro filtro.
    useEffect(() => {
        if (debouncedSearch === emittedSearch.current) return;
        emittedSearch.current = debouncedSearch;
        onChange({ ...filters, search: debouncedSearch });
    }, [debouncedSearch, filters, onChange]);

    // Baja a la caja local un reset hecho desde afuera.
    useEffect(() => {
        if (filters.search === emittedSearch.current) return;
        emittedSearch.current = filters.search;
        setSearchText(filters.search);
    }, [filters.search]);

    const patch = (partial: Partial<AgendaFilters>) => {
        onChange({ ...filters, ...partial });
    };

    const toggleFlag = (flag: AgendaFlag) => {
        const next = filters.flags.includes(flag)
            ? filters.flags.filter((item) => item !== flag)
            : [...filters.flags, flag];
        patch({ flags: next });
    };

    const clearAll = () => {
        emittedSearch.current = '';
        setSearchText('');
        onChange(emptyAgendaFilters);
    };

    return (
        <FiltersColumn
            collapsed={collapsed}
            onToggleCollapsed={onToggleCollapsed}
            activeCount={activeFilterCount(filters)}
            onReset={clearAll}
        >
            <div className={GROUP}>
                <div className={LABEL}>Búsqueda</div>
                <div className="relative">
                    <Search
                        aria-hidden
                        className="pointer-events-none absolute left-1.5 top-1/2 size-3 -translate-y-1/2 text-muted-foreground"
                    />
                    <input
                        type="search"
                        value={searchText}
                        onChange={(event) => {
                            setSearchText(event.target.value);
                        }}
                        placeholder="CR-XXXX, cliente, IMEI"
                        aria-label="Buscar por crédito, cliente o IMEI"
                        className={cn(FIELD, 'pl-6 pr-6')}
                    />
                    {searchText === '' ? null : (
                        <button
                            type="button"
                            onClick={() => {
                                emittedSearch.current = '';
                                setSearchText('');
                                patch({ search: '' });
                            }}
                            aria-label="Limpiar búsqueda"
                            className="absolute right-1 top-1/2 flex size-4 -translate-y-1/2 items-center justify-center rounded text-muted-foreground transition-colors hover:text-foreground"
                        >
                            <X className="size-3" aria-hidden />
                        </button>
                    )}
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Tienda</div>
                <select
                    value={filters.store}
                    aria-label="Tienda"
                    onChange={(event) => {
                        patch({ store: event.target.value });
                    }}
                    className={FIELD}
                >
                    <option value="">Todas</option>
                    {AGENDA_STORE_OPTIONS.map((store) => (
                        <option key={store.id} value={store.id}>
                            {store.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Días de mora</div>
                <select
                    value={filters.arrears}
                    aria-label="Días de mora"
                    onChange={(event) => {
                        patch({ arrears: event.target.value as AgendaFilters['arrears'] });
                    }}
                    className={FIELD}
                >
                    <option value="">Todos</option>
                    {ARREARS_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Canal último contacto</div>
                <select
                    value={filters.channel}
                    aria-label="Canal del último contacto"
                    onChange={(event) => {
                        patch({ channel: event.target.value as AgendaFilters['channel'] });
                    }}
                    className={FIELD}
                >
                    <option value="">Todos</option>
                    {CONTACT_CHANNEL_FILTER_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Monto vencido</div>
                <div className="grid grid-cols-2 gap-1">
                    <MoneyInput
                        value={filters.minAmountCents}
                        placeholder="Mín"
                        ariaLabel="Monto vencido mínimo"
                        onChange={(cents) => {
                            patch({ minAmountCents: cents });
                        }}
                    />
                    <MoneyInput
                        value={filters.maxAmountCents}
                        placeholder="Máx"
                        ariaLabel="Monto vencido máximo"
                        onChange={(cents) => {
                            patch({ maxAmountCents: cents });
                        }}
                    />
                </div>
            </div>

            <div className={GROUP}>
                <div className={LABEL}>Extra</div>
                <div className="flex flex-col items-start gap-[3px]">
                    {FLAG_OPTIONS.map((option) => {
                        const on = filters.flags.includes(option.value);
                        return (
                            <button
                                key={option.value}
                                type="button"
                                aria-pressed={on}
                                onClick={() => {
                                    toggleFlag(option.value);
                                }}
                                className={cn(
                                    'whitespace-nowrap rounded-full border px-1.5 py-[2px] text-[9.5px] font-semibold transition-colors',
                                    on && option.danger === true
                                        ? 'border-danger bg-danger text-white'
                                        : on
                                          ? 'border-primary bg-primary text-primary-foreground'
                                          : option.danger === true
                                            ? 'border-danger text-danger hover:bg-accent'
                                            : 'border-border text-foreground hover:bg-accent',
                                )}
                            >
                                {option.label}
                            </button>
                        );
                    })}
                </div>
            </div>
        </FiltersColumn>
    );
}
